import { useEffect, useRef, useState } from "react";
import { Button, Col, Container, Form, InputGroup, Row } from "react-bootstrap";
import { showErrorMessage } from "../../../utils";
import { FormSectionDivider, FormSectionHeader, Icon, IconDefinitions, SlideOutModal } from "../../../components";
import { ProviderModel } from "../../../data/entities";

export interface SyncCalendarModalProps {
  showState: any;
  link: string;
  userProviders?: ProviderModel[];
}

export function SyncCalendarModal(props: Readonly<SyncCalendarModalProps>) {

  const locationNames = [
    ...new Set(
      (props.userProviders ?? [])
        .map((provider) => provider.location?.name)
        .filter((name): name is string => !!name)
    ),
  ];

  const [copied, setCopied] = useState(false);
  const copiedTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (copiedTimeout.current) clearTimeout(copiedTimeout.current);
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(props.link);
      setCopied(true);
      if (copiedTimeout.current) clearTimeout(copiedTimeout.current);
      copiedTimeout.current = setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      showErrorMessage("Failed to copy the link.");
    }
  };

  return (
    <SlideOutModal
      title="Web Calendar Link"
      showState={props.showState}
      footer={
        <Container>
          <Row>
            <Col>
              <Button onClick={() => props.showState.setter(false)} variant="cancel" className="w-100">
                Close
              </Button>
            </Col>
          </Row>
        </Container>
      }
    >
      <Container className="px-3 pt-3">

        {/* Included locations */}
        <FormSectionHeader>Locations</FormSectionHeader>
        <p className="small text-body-secondary mb-2">
          Appointments from these locations are included in your exported calendar.
        </p>
        {locationNames.length > 0 ? (
          locationNames.map((name) => (
            <div key={name} className="d-flex align-items-center py-1">
              <Icon iconName={IconDefinitions.location} className="me-2 text-body-secondary" />
              <span className="fw-semibold">{name}</span>
            </div>
          ))
        ) : (
          <p className="small text-body-secondary mb-0">
            No locations are linked to your calendar yet.
          </p>
        )}

        <FormSectionDivider />

        {/* Subscription link */}
        <FormSectionHeader>Calendar Link</FormSectionHeader>
        <p className="small text-body-secondary mb-2">
          Use this link to subscribe from Outlook or any calendar app.
        </p>
        <InputGroup size="sm">
          <Form.Control
            readOnly
            value={props.link}
            className="font-monospace bg-light"
            style={{ fontSize: "0.75rem" }}
            onFocus={(e) => (e.target as HTMLInputElement).select()}
          />
          <Button
            variant={copied ? "success" : "outline-secondary"}
            onClick={handleCopy}
            title="Copy to clipboard"
          >
            <Icon iconName={copied ? IconDefinitions.check : IconDefinitions.copy} />
          </Button>
        </InputGroup>
        {copied && (
          <div className="small text-success mt-1">Copied to clipboard</div>
        )}

      </Container>
    </SlideOutModal>
  );
}

export default SyncCalendarModal;
